"use client";

/**
 * Characteristics radar (FIFA-hexagon style) for the Compare result.
 *
 * Each listing renders as one translucent polygon in its palette
 * color over a shared six-axis grid, so shape = character: a spiky
 * polygon toward "Sərfəli qiymət" is a deal with trade-offs, a fat
 * one is the all-rounder. Axes are normalized 0..100 with FIXED
 * real-world anchors (not within-set min/max), so the same flat
 * draws the same shape in every comparison:
 *
 *   deal    - overpriced% mapped -20%..+20% -> 100..0
 *   metro   - distance 0..3 km -> 100..0
 *   center  - distance 0..12 km -> 100..0
 *   area    - 40..160 m2 -> 0..100
 *   safety  - 100 minus criticality (15/point) and red flags (10 each)
 *   conf    - the model's own confidence % as-is
 *
 * Missing inputs sit at the neutral 50 rather than collapsing the
 * polygon. Geometry/paint live in RadarChart.
 */
import { useT } from "@/lib/locale-context";
import type { AnalysisResult } from "@/types";
import type { TranslationKey } from "@/lib/i18n";
import { colorForIndex } from "./palette";
import { redFlagCount } from "./metrics";
import RadarChart from "./RadarChart";

interface Props {
  results: AnalysisResult[];
}

function cf(r: AnalysisResult, key: string): number | null {
  const features = (r as unknown as { computed_features?: Record<string, number> })
    .computed_features;
  const v = features?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const NEUTRAL = 50;

interface AxisSpec {
  labelKey: TranslationKey;
  score: (r: AnalysisResult) => number;
}

const AXES: AxisSpec[] = [
  {
    labelKey: "compare.radar.deal",
    score: (r) =>
      typeof r.overpriced_pct === "number"
        ? clamp01((20 - r.overpriced_pct) / 40) * 100
        : NEUTRAL,
  },
  {
    labelKey: "compare.radar.metro",
    score: (r) => {
      const km = cf(r, "dist_to_nearest_metro_km");
      return km == null ? NEUTRAL : clamp01(1 - km / 3) * 100;
    },
  },
  {
    labelKey: "compare.radar.center",
    score: (r) => {
      const km = cf(r, "dist_to_center_km");
      return km == null ? NEUTRAL : clamp01(1 - km / 12) * 100;
    },
  },
  {
    labelKey: "compare.radar.area",
    score: (r) =>
      r.area_m2 != null ? clamp01((r.area_m2 - 40) / 120) * 100 : NEUTRAL,
  },
  {
    labelKey: "compare.radar.safety",
    score: (r) => {
      const crit = typeof r.criticality === "number" ? r.criticality : 2;
      return Math.max(0, 100 - crit * 15 - redFlagCount(r) * 10);
    },
  },
  {
    labelKey: "compare.radar.confidence",
    score: (r) =>
      typeof r.confidence_pct === "number" ? r.confidence_pct : NEUTRAL,
  },
];

export default function CompareRadar({ results }: Props) {
  const t = useT();
  if (results.length < 2) return null;

  return (
    <section className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <span className="text-sm font-semibold text-gray-900">
        {t("compare.radar.title")}
      </span>

      <div className="mx-auto flex w-full max-w-[300px] flex-1 flex-col justify-center">
        <RadarChart
          labels={AXES.map((a) => t(a.labelKey))}
          series={results.map((r, i) => ({
            hex: colorForIndex(i).hex,
            values: AXES.map((a) => a.score(r)),
          }))}
          ariaLabel={t("compare.radar.title")}
        />

        {/* Legend */}
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          {results.map((_, i) => {
            const c = colorForIndex(i);
            return (
              <span
                key={i}
                className={`inline-flex items-center gap-1 text-[11px] font-semibold ${c.label}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} aria-hidden />
                {t("compare.cards.listing_label").replace("{n}", String(i + 1))}
              </span>
            );
          })}
        </div>
        <p className="mt-1.5 text-center text-[10px] leading-relaxed text-gray-400">
          {t("compare.radar.note")}
        </p>
      </div>
    </section>
  );
}
